<template>
  <v-container>
    <v-form ref="form" v-model="isFormValid">
      <v-sheet>
        <v-sheet
          class="pa-4 d-flex justify-space-between align-center"
          style="z-index: 4; position: sticky;"
          :style="{top: $vuetify.breakpoint.smAndUp ? '64px' : '56px'}"
        >
          <div class="d-flex align-center">
            <v-btn icon large @click="$emit('cancel')" class="mr-2">
              <v-icon>mdi-arrow-left-thick</v-icon>
            </v-btn>
            <h2 class="text-h5">{{ title }}</h2>
          </div>
          <div class="d-flex flex-wrap" style="gap: 4px;">
            <v-btn
              v-if="isEditMode"
              color="error"
              outlined
              class="mr-2"
              :icon="!$vuetify.breakpoint.smAndUp"
              @click="$emit('delete')"
            >
              <v-icon :small="$vuetify.breakpoint.smAndUp">mdi-trash-can-outline</v-icon>
              <template v-if="$vuetify.breakpoint.smAndUp">刪除項目</template>
            </v-btn>
            <v-btn
              color="tiffany-9"
              :dark="isFormValid"
              elevation="0"
              :outlined="!$vuetify.breakpoint.smAndUp"
              :icon="!$vuetify.breakpoint.smAndUp"
              :disabled="!isFormValid"
              @click="save"
            >
              <v-icon :small="$vuetify.breakpoint.smAndUp">mdi-content-save-outline</v-icon>
              <template v-if="$vuetify.breakpoint.smAndUp">{{ isEditMode ? '儲存編輯' : '建立內容' }}</template>
            </v-btn>
          </div>
        </v-sheet>

        <div class="pa-4">
          <v-card outlined rounded="lg" class="pa-4 pa-md-10">
            <div class="text-h6 black--text">標題</div>
            <div class="d-flex align-center" style="gap: 10px;">
              <div>標題名稱</div>
              <v-text-field
                v-model="healthEducation.title"
                :rules="rules.title"
                outlined
                hide-details="auto"
                dense
              />
            </div>
          </v-card>

          <v-card outlined rounded="lg" class="mt-4 pa-4 pa-md-10">
            <div class="d-flex justify-space-between align-center mb-4">
              <div class="text-h6 black--text">發送內容</div>
            </div>

            <div v-for="(message, index) in healthEducation.messages" :key="message.id">
              <MessageContentCard
                :message="message"
                :type="type"
                :imgLimit="imgLimit"
                :showDelete="healthEducation.messages.length > 1"
                :rules="getMessageRules(message)"
                @update:message="healthEducation.messages[index] = $event"
                @remove="removeMessage(index)"
                @content-changed="$refs.form.validate()"
              />
            </div>

            <v-btn v-if="type === 'post-op'" color="grey-5" outlined @click="addMessage">
              <v-icon small>mdi-plus</v-icon>
              新增發送內容
            </v-btn>
          </v-card>
        </div>

      </v-sheet>
    </v-form>
  </v-container>
</template>

<script>
import MessageContentCard from './MessageContentCard.vue';

export default {
  name: 'HealthEducationEditor',
  components: {
    MessageContentCard
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    initialData: {
      type: Object,
      default: () => null,
    },
    type: {
      type: String,
      required: true,
      validator: (value) => ['pre-op', 'post-op'].includes(value),
    },
    isEditMode: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      isFormValid: false,
      healthEducation: {
        title: '',
        messages: [],
      },
      rules: {
        title: [v => !!v && !!v.trim() || '標題名稱為必填欄位'],
        required: v => (v !== undefined && v !== null && v !== '') || '此欄位為必填',
        json: v => {
          if (!v || !v.trim()) return '此欄位為必填';
          try {
            JSON.parse(v);
            return true;
          } catch {
            return 'JSON 格式不正確';
          }
        },
        min1: v => Number(v) >= 0 || '最小只能選擇零天後',
      }
    };
  },
  computed: {
    imgLimit() {
      if (this.type === 'post-op') {
        return 3
      } else {
        return 1
      }
    },
    postDefaultText() {
      return '看完診後，您是否有感到不適的地方，或是否需要反應您的狀況給醫師或診所。'
    }
  },
  methods: {
    async save() {
      const isValid = await this.$refs.form.validate();
      if (isValid) {
        this.$emit('save', this.healthEducation);
      }
    },
    getMessageRules(message) {
      const rules = {};
      const type = message.type;

      if (type === 'line_default') {
        rules.defaultText = [this.rules.required];
      } else if (type === 'line_text_image') {
        rules.textImageText = [
          () => (!!message.content.text && !!message.content.text.trim()) || (message.content.images && message.content.images.length > 0) || '圖片或文字必須填寫一項'
        ];
      } else if (type === 'line_flex') {
        rules.json = [this.rules.json];
      }

      if (this.type === 'post-op') {
        rules.frequency = [this.rules.required, this.rules.min1];
        rules.hour = [this.rules.required];
        rules.minute = [this.rules.required];
      }

      return rules;
    },
    addMessage() {
      if (this.type === 'post-op' && this.healthEducation.messages.length >= 10) {
        this.$emit('show-error', '最多只能設定10則訊息');
        return;
      }
      const nextIndex = this.healthEducation.messages.length + 1;
      this.healthEducation.messages.push({
        id: `tmp_${Date.now()}`,
        type: 'line_default',
        content: {
          text: this.postDefaultText,
          title: String(nextIndex),
          offset_direction: 'AFTER'
        }
      });
    },
    removeMessage(index) {
      if (this.healthEducation.messages.length <= 1) {
        this.$emit('show-error', '至少需要一則訊息');
        return;
      }
      this.healthEducation.messages.splice(index, 1);
    },
    initializeData() {
      if (this.initialData) {
        this.healthEducation = JSON.parse(JSON.stringify(this.initialData));
        // 為空白或 null 的 title 補上編號
        if (this.type === 'post-op' && this.healthEducation.messages) {
          this.healthEducation.messages.forEach((message, index) => {
            if (!message.content) {
              message.content = {};
            }
            if (!message.content.title || message.content.title.trim() === '') {
              message.content.title = String(index + 1);
            }
          });
        }
      } else {
        if (this.type === 'post-op') {
          this.healthEducation = {
            title: '',
            messages: [
              {
                id: `tmp_${Date.now()}`,
                type: 'line_default',
                content: {
                  text: this.postDefaultText,
                  title: '1',
                  offset_direction: 'AFTER'
                }
              }
            ],
          };
        }

        if (this.type === 'pre-op') {
          this.healthEducation = {
            title: '',
            messages: [{ id: `tmp_${Date.now()}`, type: 'text_image', content: {} }],
          };
        }
      }

      if (this.type === 'pre-op' && this.healthEducation.messages.length === 0) {
         this.healthEducation.messages.push({ id: Date.now(), type: 'text_image', content: {} });
      }
    }
  },
  created() {
    this.initializeData();
  },
};
</script>